import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function Login() {
  const { t } = useTranslation('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      // Get authorization URL from backend
      const response = await axios.post(`${API_URL}/api/auth/google/authorize`);
      
      if (response.data.authorization_url) {
        // Redirect to Google login
        window.location.href = response.data.authorization_url;
      } else {
        toast.error(t('toast.googleFailed'));
      }
    } catch (error) {
      console.error('Google login error:', error);
      const errorMsg = error.response?.data?.detail || t('toast.googleNotConfigured');
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login/local`,
        { username, password },
        { withCredentials: true }
      );

      if (response.data.success && response.data.user) {
        login(response.data.user);
        toast.success(t('toast.success'));
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || t('toast.invalidCredentials');
      
      // Check if account is deactivated - show specific message
      if (error.response?.status === 403 && (error.response?.headers?.['x-account-status'] === 'inactive'
        || errorMessage.includes('deactivated'))) {
        toast.error(t('toast.deactivated'));
        navigate('/inactive', { replace: true });
        return;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative">
      <div className="absolute top-4 right-4 z-20 rounded-lg bg-white/90 dark:bg-slate-800/90 shadow-md backdrop-blur-sm">
        <LanguageSwitcher />
      </div>
      {/* Left side - Login form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-slate-900 dark:text-white mb-2" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
              FinOps
            </h1>
            <p className="text-slate-700 dark:text-slate-200 mt-4 text-lg font-medium" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
              {t('tagline')}
            </p>
          </div>

          {/* Local Login Form */}
          <div className="mt-12 bg-white/90 dark:bg-slate-800/90 rounded-xl shadow-lg border border-blue-200 dark:border-slate-700 p-8 backdrop-blur-sm">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-slate-700 dark:from-blue-400 dark:to-slate-300 bg-clip-text text-transparent mb-6 text-center" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
              {t('form.title')}
            </h2>
            
            <form onSubmit={handleLocalLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">{t('form.username')}</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('form.usernamePlaceholder')}
                  required
                  data-testid="username-input"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="password">{t('form.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('form.passwordPlaceholder')}
                  required
                  data-testid="password-input"
                  className="mt-1"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200 py-2.5"
                data-testid="login-submit-btn"
              >
                {loading ? t('form.submitting') : t('form.submit')}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-300 dark:border-slate-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400">{t('form.orContinueWith')}</span>
              </div>
            </div>

            <button
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
              className="w-full bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 dark:from-slate-600 dark:to-slate-700 dark:hover:from-slate-700 dark:hover:to-slate-800 text-white border border-slate-400 dark:border-slate-600 rounded-lg px-6 py-2.5 shadow-md hover:shadow-lg transition-all duration-200 font-semibold flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t('form.google')}
            </button>

            <p className="mt-6 text-sm text-center text-slate-500 dark:text-slate-400">
              {t('form.hint')}
            </p>
          </div>

          {/* Logo footer */}
          <div className="flex flex-col items-center pt-8 mt-8 border-t border-slate-200 dark:border-slate-700">
            <a 
              href="https://anyops.uz" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="/images/logo.png" 
                alt={t('footer.logoAlt')} 
                className="h-16 w-auto drop-shadow-lg"
              />
            </a>

            <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
              © {new Date().getFullYear() === 2026 ? '2026' : '2026'} AnyOps. {t('footer.rights')}
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Creative accounting illustration */}
      <div className="hidden lg:block lg:flex-1 relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-slate-800">
        {/* Decorative circles */}
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-slate-600/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-blue-400/10 rounded-full blur-2xl"></div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>

        {/* Floating accounting icons */}
        <div className="absolute inset-0">
          {/* Calculator icon */}
          <div className="absolute top-[12%] left-[15%] w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 shadow-lg" style={{ animation: 'float 6s ease-in-out infinite' }}>
            <svg className="w-8 h-8 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V18zm2.498-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zM8.25 6h7.5v2.25h-7.5V6zM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0012 2.25z" />
            </svg>
          </div>

          {/* Chart/graph icon */}
          <div className="absolute top-[8%] right-[20%] w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 shadow-lg" style={{ animation: 'float 7s ease-in-out infinite 1s' }}>
            <svg className="w-7 h-7 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
          </div>

          {/* Coin/dollar icon */}
          <div className="absolute bottom-[25%] left-[10%] w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 shadow-lg" style={{ animation: 'float 5s ease-in-out infinite 0.5s' }}>
            <svg className="w-6 h-6 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          {/* Document icon */}
          <div className="absolute bottom-[15%] right-[15%] w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 shadow-lg" style={{ animation: 'float 8s ease-in-out infinite 2s' }}>
            <svg className="w-7 h-7 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          </div>

          {/* Wallet icon */}
          <div className="absolute top-[45%] left-[8%] w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20" style={{ animation: 'float 6s ease-in-out infinite 3s' }}>
            <svg className="w-5 h-5 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 110-6h.75A2.25 2.25 0 0118 6v.008c0 .622.218 1.186.568 1.617M21 12v6.75A2.25 2.25 0 0118.75 21H5.25A2.25 2.25 0 013 18.75V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25v6.75z" />
            </svg>
          </div>

          {/* Trending up icon */}
          <div className="absolute top-[30%] right-[8%] w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20" style={{ animation: 'float 7s ease-in-out infinite 1.5s' }}>
            <svg className="w-6 h-6 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
            </svg>
          </div>

          {/* Small decorative dots */}
          <div className="absolute top-[20%] left-[45%] w-2 h-2 bg-white/30 rounded-full"></div>
          <div className="absolute top-[55%] right-[35%] w-3 h-3 bg-white/20 rounded-full"></div>
          <div className="absolute bottom-[40%] left-[30%] w-2 h-2 bg-white/25 rounded-full"></div>
          <div className="absolute top-[70%] right-[45%] w-2 h-2 bg-white/20 rounded-full"></div>
        </div>

        {/* Center content card */}
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-md text-center">
            {/* Large illustration - pie chart SVG */}
            <div className="mb-8 flex justify-center">
              <svg className="w-32 h-32 drop-shadow-2xl" viewBox="0 0 120 120" fill="none">
                {/* Pie chart segments */}
                <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="20" />
                <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="20" strokeDasharray="78.5 235.5" strokeDashoffset="0" strokeLinecap="round" />
                <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(147,197,253,0.8)" strokeWidth="20" strokeDasharray="62.8 251.2" strokeDashoffset="-78.5" strokeLinecap="round" />
                <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(96,165,250,0.6)" strokeWidth="20" strokeDasharray="47.1 266.9" strokeDashoffset="-141.3" strokeLinecap="round" />
                <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(59,130,246,0.4)" strokeWidth="20" strokeDasharray="125.6 188.4" strokeDashoffset="-188.4" strokeLinecap="round" />
                {/* Center circle */}
                <circle cx="60" cy="60" r="28" fill="rgba(30,64,175,0.6)" />
                <text x="60" y="56" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="sans-serif">FIN</text>
                <text x="60" y="70" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="7" fontFamily="sans-serif">{t('hero.chartLabel')}</text>
              </svg>
            </div>

            <h2
              className="text-4xl font-bold mb-4 text-white"
              style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}
            >
              {t('hero.title')}
            </h2>
            <p
              className="text-lg text-blue-100/90 leading-relaxed mb-8"
              style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}
            >
              {t('hero.subtitle')}
            </p>

            {/* Feature pills */}
            <div className="flex flex-wrap justify-center gap-3">
              <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white/90 border border-white/20">
                📊 {t('hero.reports')}
              </span>
              <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white/90 border border-white/20">
                💰 {t('hero.budgets')}
              </span>
              <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white/90 border border-white/20">
                📈 {t('hero.analytics')}
              </span>
              <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white/90 border border-white/20">
                🔒 {t('hero.secure')}
              </span>
            </div>
          </div>
        </div>

        {/* Float animation keyframes */}
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-15px); }
          }
        `}</style>
      </div>
    </div>
  );
}

export default Login;
